// Pop-out-rammeverk: hvert SKADE-panel kan løsrives til eget vindu/skjerm.
// Et popp-ut-vindu laster samme app-bundle med ?panel=<id> og rendrer KUN det
// panelet (frittstående-modus), mens det deler sanntidstilstand via SyncBus.

import { BUILD_ID } from '../ui/shell.js';

export interface PanelDef {
  id: string;
  title: string;
}

/** Panelene som kan poppes ut (utvides etter hvert: kart, chat, PTT, intel …). */
export const POPOUTS: Record<string, PanelDef> = {
  roster: { id: 'roster', title: 'Objektliste' },
  c2: { id: 'c2', title: 'Stridsmeldinger' },
  intel: { id: 'intel', title: 'Etterretning' },
  chat: { id: 'chat', title: 'Chat / TOC' },
  feltveksler: { id: 'feltveksler', title: 'Feltveksler' },
  directory: { id: 'directory', title: 'Kontakter' },
  checklist: { id: 'checklist', title: 'Kapabilitets-sjekkliste' },
  eso: { id: 'eso', title: 'ESO — Styrkeoppbygging' },
  rfi: { id: 'rfi', title: 'RFI / innsamlingsstyring' },
  dispatch: { id: 'dispatch', title: 'Dispatch-kø' },
  perstat: { id: 'perstat', title: 'PERSTAT / sanitet' },
  logistics: { id: 'logistics', title: 'Logistikk / forsyning' },
  admin: { id: 'admin', title: 'Administrasjon' },
  personregister: { id: 'personregister', title: 'Personregister' },
  kapabilitetsplanlegger: { id: 'kapabilitetsplanlegger', title: 'Kapabiliteter' },
  orbat: { id: 'orbat', title: 'ORBAT-creator' },
  planprosess: { id: 'planprosess', title: 'Utvikling til Strid' },
};

/** Kart mellom et registrert vindus CSS-klasse og pop-out-nøkkelen (for ⤢-knapp). */
export const POPOUT_KEY_BY_CLASS: Record<string, string> = {
  'sb-panel': 'feltveksler',
  'directory-panel': 'directory',
  'chat-panel': 'chat',
  'checklist-panel': 'checklist',
  'intel-host': 'intel',
  'admin-panel': 'admin',
  'personregister-panel': 'personregister',
  'kapabilitetsplanlegger-panel': 'kapabilitetsplanlegger',
  'eso-panel': 'eso',
  'orbat-creator': 'orbat',
  'rfi-panel': 'rfi',
  'dispatch-panel': 'dispatch',
  'casualty-panel': 'perstat',
  'logistics-panel': 'logistics',
  'plan-panel': 'planprosess',
};

/**
 * Planprosessen er bredere (520 px) og høyere enn de øvrige panelene, så
 * standardvinduet på 460×780 ville tvunget fram vannrett rulling på et panel som
 * er bygget for å vise fem trinn ved siden av hverandre.
 */
const VINDU_STORRELSE: Record<string, { w: number; h: number }> = {
  planprosess: { w: 600, h: 900 },
};

/** Hvilket panel er dette vinduet bedt om å vise? null = full COP. */
export function standalonePanelId(): string | null {
  try {
    const id = new URLSearchParams(location.search).get('panel');
    return id && POPOUTS[id] ? id : null;
  } catch {
    return null;
  }
}

/**
 * Åpne et panel i eget vindu (navngitt så gjentatt trykk gjenbruker vinduet).
 *
 * ⚠ `b=<BUILD_ID>` I URL-EN ER IKKE PYNT. To feil krever den:
 *
 *  1. Et NAVNGITT vindu gjenbrukes av nettleseren. Er URL-en identisk, blir det
 *     gamle dokumentet bare løftet fram — også når hovedvinduet har lastet en
 *     helt ny bygg-versjon. Pop-outen kunne dermed kjøre gammel kode mot ny
 *     server, i det uendelige, uten at noe så galt ut.
 *  2. Er `index.html` cachet (COP-en kjørte på nginx sin standardkonfig uten
 *     cache-styring — se deploy/cop.nginx.conf), peker det gamle dokumentet på
 *     hashede bundles som ikke finnes lenger. CSS-en 404-er, og vinduet rendres
 *     HELT USTYLET. Det var det eieren så: «Eget vindu er bare søppel!»
 *
 * Med bygg-id-en i URL-en endres adressen ved hver deploy, så vinduet NAVIGERER
 * i stedet for å bli gjenbrukt — pop-outen kan aldri være en annen versjon enn
 * hovedvinduet.
 */
export function popOut(id: string): void {
  const def = POPOUTS[id];
  if (!def) return;
  const url = `${location.pathname}?panel=${encodeURIComponent(id)}`
    + `&b=${encodeURIComponent(BUILD_ID)}`;
  const { w, h } = VINDU_STORRELSE[id] ?? { w: 460, h: 780 };
  // Klem mot skjermen: et vindu høyere enn skjermen får rullefelt på hele siden.
  const bredde = Math.min(w, Math.max(320, (globalThis.screen?.availWidth ?? w) - 40));
  const hoyde = Math.min(h, Math.max(320, (globalThis.screen?.availHeight ?? h) - 40));
  window.open(url, `skade-${id}`, `width=${bredde},height=${hoyde},menubar=no,toolbar=no,location=no`);
}
